import React, { useState, useEffect } from "react";
import loadable from "@loadable/component";
import { useIsMobile } from "../../hooks";

import {
  productStats,
  productStatsResponsive,
  filterImoji,
} from "../../static-data";
import { FILTER_NAMES } from "../../../shared/enums";

const LazyImg = loadable(() => import("../common/LazyImg"));
const FadeIn = loadable(() => import("../common/FadeIn"));

const StatsItem = ({ page, type, isBold, value, label, filterType }) => {
  if (!value) return null;

  const isCurrentClass = type.includes(page) ? " list-stats--current" : "";
  const isBoldClass = isBold ? " bold-text" : "";

  // For airMini: Only BB and BS get grey box for N/A, CC and OR show plain N/A
  // For air: Keep original behavior (all N/A get grey box)
  const shouldHideNA = filterType === "airMini"
    ? (value.includes("N/A") || value.includes("Tests in progress")) && (page === "BB" || page === "BS")
    : value.includes("N/A") || value.includes("Tests in progress");

  const hasNovalueClass = shouldHideNA ? " list-stats--hidden" : "";
  const isCenteredClass =
    label.includes("N-UNDECANE") && value.includes("N/A")
      ? " list-stats--hidden-center"
      : "";
  const isCenteredInprocessClass =
    label.includes("N-UNDECANE") && value.includes("Tests in progress")
      ? " list-stats--hidden-center list-stats--vertical"
      : "";

  return (
    <li
      className={`${isCurrentClass}${isBoldClass}${hasNovalueClass}${isCenteredClass}${isCenteredInprocessClass}`}
    >
      <span className="stats-item__label">{`The ${FILTER_NAMES[page]}`}</span>
      {value}
    </li>
  );
};

const StatsList = ({ label, stats, type, filterType }) => {
  // Only show products that have data in stats
  const availableProducts = Object.keys(filterImoji).filter((key) => stats[key]);

  return (
    <ul className="stats-item__list">
      {availableProducts.map((key) => {
        const statsData = {
          page: key,
          label,
          type,
          filterType,
          ...stats[key],
        };
        return <StatsItem key={key} {...statsData} />;
      })}
    </ul>
  );
};

const ProductStats = ({ type = "CB" }) => {
  const [filterType, setFilterType] = useState("air");
  const isMobile = useIsMobile(1024);
  return (
    <div
      className={`p-product-stats ${filterType === "airMini" ? "p-product-stats--air-mini" : ""}`}
      style={{ "--primary-color": " #F42C52", "--secondary-color": "#FFF2F5" }}
    >
      <div className="container">
        <div className="p-product-stats__header">
          <ProductStatsTab
            filterType={filterType}
            setFilterType={setFilterType}
            isMobile={isMobile}
          />
        </div>
        {!isMobile ? (
          <div className="p-product-stats__table">
            <div className="p-product-stats__col p-product-stats__col--label">
              <ul className="p-product-stats__labels">
                {productStats[filterType].map(({ label }) => (
                  <li key={label}>
                    {label.includes("CADR") ? (
                      <>
                        {label} <small>(m3/hr)</small>
                      </>
                    ) : (
                      label
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="p-product-stats__col p-product-stats__col--values stats-grid">
              <div className="stats-grid__head">
                {(() => {
                  // Get available products from the first stats item
                  const firstStats = productStats[filterType][0]?.stats || {};
                  const availableProducts = Object.keys(filterImoji).filter((key) => firstStats[key]);

                  return availableProducts.map((key) => (
                    <div
                      key={key}
                      className={`p-product-stats__column stats-item${
                        key === type ? " stats-item--current" : ""
                      }`}
                    >
                      <h3 className="stats-item__title">
                        <FadeIn>
                          <LazyImg
                            src={filterImoji[key]}
                            alt={FILTER_NAMES[key]}
                          />
                        </FadeIn>{" "}
                        The {FILTER_NAMES[key]}
                      </h3>
                    </div>
                  ));
                })()}
              </div>
              <div className="stats-grid__body">
                {productStats[filterType].map((statsItem) => (
                  <StatsList {...statsItem} type={type} filterType={filterType} key={statsItem.label} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ProductStatsResponsive filterType={filterType} />
        )}
      </div>
    </div>
  );
};

function ProductStatsTab({ filterType, setFilterType, isMobile }) {
  return (
    <div className="p-product-stats__tab">
      <ul className="p-product-stats__tab-list">
        <li>
          <button
            type="button"
            className={`p-product-stats__tab-button ${
              filterType === "air" ? "active" : ""
            }`}
            onClick={() => setFilterType("air")}
          >
            {isMobile ? "Mila Air 3" : "Air 3"}
          </button>
        </li>
        <li>
          <button
            type="button"
            className={`p-product-stats__tab-button ${
              filterType === "airMini" ? "active" : ""
            }`}
            onClick={() => setFilterType("airMini")}
          >
            {isMobile ? "Mila Air Mini" : "Air Mini"}
          </button>
        </li>
      </ul>
    </div>
  );
}

function ProductStatsResponsive({ filterType }) {
  const { image, description } = productStatsResponsive;
  const [activeItem, setActiveItem] = useState(0);
  
  // Reset activeItem when filterType changes to prevent out-of-bounds errors
  useEffect(() => {
    setActiveItem(0);
  }, [filterType]);
  
  // Safety check to ensure activeItem is valid
  const currentData = productStatsResponsive[filterType];
  const safeActiveItem = currentData && activeItem < currentData.length ? activeItem : 0;
  const { stats } = currentData && currentData[safeActiveItem] ? currentData[safeActiveItem] : { stats: {} };

  return (
    <div className="p-stats-table">
      <div className="p-stats-table__header">
        <div className="p-stats-table__tab">
          <ul className="p-stats-table__tab-list">
            {currentData && currentData.map(
              ({ label, type }, index) => (
                <li
                  key={index}
                  className={safeActiveItem === index ? "active" : ""}
                >
                  <button type="button" onClick={() => setActiveItem(index)}>
                    {label && <strong>{label}</strong>}
                    {type && <span>{type}</span>}
                  </button>
                </li>
              )
            )}
          </ul>
        </div>
      </div>
      <div className="p-stats-table__Body">
        {Object.keys(FILTER_NAMES)
          .filter((key) => stats[key] && stats[key].value)
          .map((key) => (
            <div key={key} className="p-stats-table__item p-stats-item">
              <div className="p-stats-item__header">
                <FadeIn>
                  <LazyImg
                    className="p-stats-item__img"
                    src={image[key]}
                    alt={FILTER_NAMES[key]}
                  />
                </FadeIn>
                <h3 className="p-stats-item__title">
                  The{" "}
                  <FadeIn>
                    <LazyImg src={filterImoji[key]} alt={FILTER_NAMES[key]} />
                  </FadeIn>{" "}
                  {FILTER_NAMES[key]}.
                </h3>
                {description[key] && (
                  <p className="p-stats-item__description">{description[key]}</p>
                )}
              </div>
              {stats[key] && stats[key].value && (
                <div className="p-stats-item__values">
                  {stats[key].isBold ? (
                    <strong>{stats[key].value}</strong>
                  ) : (
                    stats[key].value
                  )}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

export default ProductStats;
